import ThemedModal from '~/components/ThemedModal';
import {
    useBookableResourcesQuery,
    useEventTypesQuery,
} from '~/lib/SharedQueries';
import { useContext, useEffect, useMemo, useState } from 'react';
import Select from '~/components/Select';
import TextInput from '~/components/TextInput';
import EmailInput from '~/components/EmailInput';
import useSharedPostApi from '~/lib/useSharedPostApi';
import * as Toast from '~/lib/Toast';
import { FormContext } from '~/components/forms/FormContext';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import useClientTimezone from '~/public/lib/useClientTimezone.js';
import { addDays, endOfDay } from 'date-fns';
import {
    dateStringToDateTime,
    formatDateLong,
    formatInternalDate,
} from '~/lib/DateTime';

// The window we watch for openings by default, and the furthest out a client
//  can push it (every entry is re-checked on each notification run)
export const DEFAULT_WAITLIST_WINDOW_DAYS = 30;
export const MAX_WAITLIST_WINDOW_DAYS = 90;

export function useIsWaitlistEnabled(settings) {
    const { features } = useContext(FormContext);
    const isWaitlistFeatureEnabled = !!features?.waitlist;
    return !settings?.disableWaitlist && isWaitlistFeatureEnabled;
}

const AddedToWaitlist = ({ eventType, startDate, endDate }) => {
    return (
        <div className="flex flex-col items-center justify-center">
            <CheckCircleIcon className="w-20 h-20 themed-text" />
            <div className="text-lg font-bold mt-4">
                You’re on the waitlist.
            </div>
            <div className="text-gray-500 mt-4 text-center">
                {(!eventType || eventType.is_class) && (
                    <>
                        We will notify you once a spot opens up for{' '}
                        {eventType?.name || 'this'}.
                    </>
                )}
                {!!eventType && !eventType?.is_class && (
                    <>
                        We will notify you if any spots open up for{' '}
                        {eventType?.name} between{' '}
                        {formatDateLong(dateStringToDateTime(startDate))} and{' '}
                        {formatDateLong(dateStringToDateTime(endDate))}.
                    </>
                )}
            </div>
        </div>
    );
};

function defaultDateRange() {
    const today = new Date();
    return {
        startDate: formatInternalDate(today),
        endDate: formatInternalDate(
            addDays(today, DEFAULT_WAITLIST_WINDOW_DAYS),
        ),
    };
}

const AddToWaitlistModal = ({
    isOpen,
    onClose,
    eventType,
    bookableResource,
    start,
    settings,
}) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [{ startDate, endDate }, setDateRange] = useState(defaultDateRange);

    const [hasAddedToWaitlist, setHasAddedToWaitlist] = useState(false);

    const { submission } = useContext(FormContext);
    const prefillClient = submission?.formData?.loggedInClient || {};

    const clientTz = useClientTimezone();

    useEffect(() => {
        if (isOpen && prefillClient) {
            setName(prefillClient.name);
            setEmail(prefillClient.email);
        }
    }, [isOpen]);

    useEffect(() => {
        setHasAddedToWaitlist(false);
    }, [eventType, bookableResource]);

    const { data: eventTypesData } = useEventTypesQuery();

    const eventTypeDetails = (eventTypesData || []).find(
        et => parseInt(et.id) === parseInt(eventType),
    );

    // Only worth a request when we're actually narrowed to one resource and the
    //  form isn't set up to keep resources out of sight. The leading arguments
    //  are left undefined so this shares the all-resources query others fetch.
    const showsBookableResource =
        !!bookableResource && !settings?.hideBookableResource;
    const { data: bookableResourcesData } = useBookableResourcesQuery(
        undefined,
        undefined,
        undefined,
        { disabled: !showsBookableResource },
    );

    const bookableResourceDetails = (bookableResourcesData || []).find(
        br => parseInt(br.id) === parseInt(bookableResource),
    );

    // Classes are waitlisted for one specific instance, so there's nothing to
    //  pick. Everything else watches a window of time the client chooses.
    const canPickDateRange =
        !start && !!eventTypeDetails && !eventTypeDetails.is_class;

    // yyyy-MM-dd sorts the same as the dates it represents, so it compares directly
    const { startDateError, endDateError } = useMemo(() => {
        if (!canPickDateRange) {
            return {};
        }
        return {
            startDateError: !startDate ? 'Enter a start date.' : undefined,
            endDateError: !endDate
                ? 'Enter an end date.'
                : startDate && endDate <= startDate
                  ? 'The end date must be after the start date.'
                  : undefined,
        };
    }, [canPickDateRange, startDate, endDate]);

    const hasDateRangeError = !!(startDateError || endDateError);

    const { save, isSaving } = useSharedPostApi(`/waitlist/add`);

    function joinWaitlist(ev) {
        ev.preventDefault();
        ev.stopPropagation();

        if (hasDateRangeError) {
            return;
        }

        save({
            eventType,
            bookableResource,
            // Send the exact instants the client's dates mean to them, rather
            //  than bare dates the server would read in its own time zone
            start: canPickDateRange
                ? dateStringToDateTime(startDate).toISOString()
                : start,
            end: canPickDateRange
                ? endOfDay(dateStringToDateTime(endDate)).toISOString()
                : undefined,
            url: document.location.href,
            timezone: clientTz,
            client: {
                name,
                email,
                phone,
            },
        }).then(result => {
            //Toast.success(result.message)
            setHasAddedToWaitlist(true);
            //onClose();
        });
    }

    return (
        <ThemedModal isOpen={isOpen} onClose={onClose}>
            {hasAddedToWaitlist && (
                <AddedToWaitlist
                    eventType={eventTypeDetails}
                    startDate={startDate}
                    endDate={endDate}
                />
            )}
            {!hasAddedToWaitlist && (
                <>
                    <div className="mb-6">
                        <h2>{eventTypeDetails?.name}</h2>
                        {showsBookableResource && !!bookableResourceDetails && (
                            <div className="text-gray-500 text-center">
                                {bookableResourceDetails.name}
                            </div>
                        )}
                    </div>

                    <div>
                        <TextInput
                            label="Name"
                            id="client.name"
                            onChange={setName}
                            value={name}
                            autoFocus={!name}
                            className="mb-4"
                        />

                        <div className="input-label">Email</div>
                        <EmailInput
                            id="client.email"
                            onChange={setEmail}
                            value={email}
                            className="mb-4"
                        />

                        <TextInput
                            type="text"
                            id="client.phone"
                            label="Phone"
                            onChange={setPhone}
                            value={phone}
                            className="mb-4"
                        />

                        {canPickDateRange && (
                            <>
                                <div className="text-gray-500 mb-2">
                                    Which dates should we watch for openings?
                                </div>
                                <div className="flex gap-4 mb-4">
                                    <TextInput
                                        type="date"
                                        id="waitlist.start"
                                        label="Start date"
                                        className="flex-1"
                                        value={startDate}
                                        error={startDateError}
                                        min={formatInternalDate(new Date())}
                                        onChange={newStartDate =>
                                            setDateRange(current => ({
                                                ...current,
                                                startDate: newStartDate,
                                            }))
                                        }
                                    />
                                    <TextInput
                                        type="date"
                                        id="waitlist.end"
                                        label="End date"
                                        className="flex-1"
                                        value={endDate}
                                        error={endDateError}
                                        min={startDate || undefined}
                                        max={
                                            startDate
                                                ? formatInternalDate(
                                                      addDays(
                                                          dateStringToDateTime(
                                                              startDate,
                                                          ),
                                                          MAX_WAITLIST_WINDOW_DAYS,
                                                      ),
                                                  )
                                                : undefined
                                        }
                                        onChange={newEndDate =>
                                            setDateRange(current => ({
                                                ...current,
                                                endDate: newEndDate,
                                            }))
                                        }
                                    />
                                </div>
                            </>
                        )}

                        <button
                            className="form-button"
                            type="button"
                            disabled={hasDateRangeError || isSaving}
                            onClick={joinWaitlist}
                        >
                            {isSaving ? 'Joining...' : 'Join Waitlist'}
                        </button>
                    </div>
                </>
            )}
        </ThemedModal>
    );
};

export default AddToWaitlistModal;
